// Deposit-zap BROWSER execution layer. Wraps the pure zap-core logic with the
// LI.FI HTTP API (fetch) + the user's ethers signer. NO SDK bundle, NO custom
// contract. Two-step "fund your HyperEVM wallet once" model: bridge any token/
// chain → vault USDC + a HYPE gas sliver into the user's OWN wallet, then normal
// deposits reuse that USDC + HYPE.

import {
  LIFI_BASE, HYPEREVM_CHAIN_ID, buildBridgeQuoteParams, validateQuote,
  parseFees, extractTxRequest, isVaultUsdc, assertDestTokenIsVaultUsdc,
  buildGasLegParams, sizeGasLeg, splitDepositForGas, validateGasLeg,
  assertDestTokenIsNativeHype, isNativeHype,
} from './zap-core.mjs';
import { getSigner } from './wallet.js';

// USDC address per supported source chain. Only chains LI.FI can bridge to 999
// from + where we can name the canonical USDC. Unknown chain → manual-bridge msg.
export const KNOWN_USDC = {
  1:     { addr: "0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48", name: "Ethereum" },
  42161: { addr: "0xaf88d065e77c8cC2239327C5EDb3A432268e5831", name: "Arbitrum" },
  10:    { addr: "0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85", name: "Optimism" },
  8453:  { addr: "0x833589fCD6eDb6E08f4c7C32D4f71b54bdA02913", name: "Base" },
  137:   { addr: "0x3c499c542cEF5E3811e1192ce70d8cC03d5c3359", name: "Polygon" },
  56:    { addr: "0x8AC76a51cc950d9822D68b83fE1Ad97B32Cd580d", name: "BSC" },
};

const ERC20_MIN = [
  "function allowance(address,address) view returns (uint256)",
  "function approve(address,uint256) returns (bool)",
  "function balanceOf(address) view returns (uint256)",
];

async function _lifi(path, params, timeoutMs = 15000) {
  const url = `${LIFI_BASE}${path}?` + new URLSearchParams(params).toString();
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);   // never hang on a slow LI.FI call
  let r;
  try { r = await fetch(url, { signal: ctrl.signal }); }
  catch (e) { throw new Error(e?.name === 'AbortError' ? `LI.FI ${path} timed out` : (e?.message || `LI.FI ${path} failed`)); }
  finally { clearTimeout(t); }
  const body = await r.json();
  if (!r.ok || body.message && !body.estimate) {
    const e = new Error(body.message || `LI.FI ${path} HTTP ${r.status}`);
    e.lifiCode = body.code; e.httpStatus = r.status; throw e;
  }
  return body;
}

// Wait for a sent tx to confirm, but NEVER forever. A dropped/replaced/stuck tx
// would otherwise hang the "Bridging…" screen indefinitely (it lies that funds are
// moving when nothing is). On timeout we throw TX_TIMEOUT so the UI can recover to
// an honest "didn't confirm — your funds are safe, retry" state.
async function _waitTx(sent, ms = 120000) {
  let timer;
  const timeout = new Promise((_, rej) => { timer = setTimeout(() => rej(Object.assign(new Error('Transaction did not confirm in time'), { code: 'TX_TIMEOUT' })), ms); });
  try { return await Promise.race([sent.wait(), timeout]); }
  finally { clearTimeout(timer); }
}

// Fetch source-token USD price + decimals (for refuel sizing). Returns
// { priceUSD, decimals } or null.
export async function getSourceTokenInfo(chainId, tokenAddr) {
  try {
    const t = await _lifi("/token", { chain: String(chainId), token: tokenAddr });
    return { priceUSD: parseFloat(t.priceUSD), decimals: Number(t.decimals), symbol: t.symbol };
  } catch { return null; }
}

// 🔴 Two-route zap quote (the robust design — see DEPOSIT_ZAP_RUNBOOK.md §5).
// Splits the deposit into a USDC leg (bulk → vault USDC) and a GAS leg (a ~$0.60
// sliver → native HYPE), each its own Across route, each guarded. Gas delivery no
// longer depends on the fragile GasZip refuel add-on.
//
// Returns { ok, usdc:{quote,fees,toAmountMin}, gas:{quote,hypeOut}|null,
//           gasAvailable:bool, split, reason?, message? }. Graceful degrade: if
// the USDC leg is fine but the gas leg can't route, returns ok:true with
// gasAvailable:false so the UI can warn instead of stranding the user. Never
// throws on a LI.FI "no route".
export async function quoteZap({ fromChainId, fromToken, fromAmountWei, userAddr, depositUsd, minReceiveUsd = 0 }) {
  const info = await getSourceTokenInfo(fromChainId, fromToken);
  const gasLegWei = sizeGasLeg({
    fromTokenPriceUsd: info && info.priceUSD > 0 ? info.priceUSD : 1.0,
    fromTokenDecimals: info ? info.decimals : 6,
  });

  // Split the deposit; a deposit smaller than the gas sliver can't proceed.
  let split;
  try {
    split = splitDepositForGas({ totalWei: fromAmountWei, gasLegWei });
  } catch (e) {
    return { ok: false, reason: "too-small", message: e.message };
  }

  // USDC leg — must succeed (it's the deposit).
  let usdcQuote;
  try {
    usdcQuote = await _lifi("/quote", buildBridgeQuoteParams({
      fromChain: fromChainId, fromToken, fromAmount: split.usdcLegWei, fromAddress: userAddr,
    }));
  } catch (e) {
    return { ok: false, reason: "no-route", message: e.message };
  }
  const uv = validateQuote(usdcQuote, { depositUsd, minReceiveUsd });
  if (!uv.ok) return { ok: false, reason: uv.reason, message: uv.message, got: uv.got, quote: usdcQuote };

  // Gas leg — best-effort. If it can't route, degrade gracefully (don't block).
  let gas = null, gasAvailable = false;
  try {
    const gasQuote = await _lifi("/quote", buildGasLegParams({
      fromChain: fromChainId, fromToken, fromAmount: split.gasLegWei, fromAddress: userAddr,
    }));
    const gv = validateGasLeg(gasQuote);
    if (gv.ok) { gas = { quote: gasQuote, hypeOut: gv.hypeOut }; gasAvailable = true; }
  } catch { /* gas leg unavailable → degrade */ }

  return {
    ok: true, gasAvailable, split,
    usdc: { quote: usdcQuote, fees: parseFees(usdcQuote), toAmountMin: uv.toAmountMin },
    gas,
  };
}

// Approve (grouped by spender) + send both legs, then return their tx hashes.
// Guards each leg before signing. If gas is null (degraded), sends only the USDC
// leg. onStep(label) reports progress. Returns { usdcTxHash, gasTxHash|null }.
export async function executeZap({ usdc, gas, fromToken, split, onStep }) {
  // 🔴 Final guards before any signature — never send a wrong-token route.
  assertDestTokenIsVaultUsdc(usdc.quote);
  if (gas) assertDestTokenIsNativeHype(gas.quote);

  const signer = getSigner();
  if (!signer) throw new Error("Connect your wallet first.");
  const owner = await signer.getAddress();

  const legs = [{ name: "funds", quote: usdc.quote, amountWei: split.usdcLegWei }];
  if (gas) legs.push({ name: "gas", quote: gas.quote, amountWei: split.gasLegWei });

  // ERC-20 approvals, grouped by spender (both legs usually share LI.FI's
  // executor → one approval for the sum). Native source tokens skip this.
  const isNative = /^0x0{40}$/i.test(fromToken) || /^0xeeee/i.test(fromToken);
  if (!isNative) {
    const needBySpender = {};
    for (const leg of legs) {
      const sp = leg.quote.estimate?.approvalAddress || extractTxRequest(leg.quote).to;
      needBySpender[sp] = (needBySpender[sp] || 0n) + BigInt(leg.amountWei);
    }
    const token = new ethers.Contract(fromToken, ERC20_MIN, signer);
    for (const [sp, total] of Object.entries(needBySpender)) {
      if ((await token.allowance(owner, sp)) < total) {
        onStep?.("Approve the bridge in your wallet…");
        await _waitTx(await token.approve(sp, total));
      }
    }
  }

  // Send each leg sequentially (two wallet confirmations: funds, then gas).
  const hashes = {};
  for (const leg of legs) {
    onStep?.(leg.name === "gas" ? "Confirm the gas top-up in your wallet…" : "Confirm the bridge in your wallet…");
    const tx = extractTxRequest(leg.quote);
    const sent = await signer.sendTransaction({
      to: tx.to, data: tx.data, value: tx.value ? BigInt(tx.value) : 0n,
      ...(tx.gasLimit ? { gasLimit: BigInt(tx.gasLimit) } : {}),
    });
    onStep?.(leg.name === "gas" ? "Sending gas… (safe to wait)" : "Bridging your funds… (safe to wait)");
    await _waitTx(sent);
    hashes[leg.name === "gas" ? "gasTxHash" : "usdcTxHash"] = sent.hash;
  }
  return { usdcTxHash: hashes.usdcTxHash, gasTxHash: hashes.gasTxHash || null };
}

// Poll a single leg's LI.FI status until DONE/FAILED/TIMEOUT.
async function pollLeg({ txHash, fromChainId, onStep, timeoutMs = 600000, intervalMs = 5000 }) {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    let s;
    try {
      s = await _lifi("/status", { txHash, fromChain: String(fromChainId), toChain: String(HYPEREVM_CHAIN_ID) });
    } catch { /* transient — keep polling */ }
    const st = s?.status;
    if (st === "DONE") return { status: "DONE", receivingTxHash: s?.receiving?.txHash };
    if (st === "FAILED") return { status: "FAILED", substatus: s?.substatusMessage };
    onStep?.(st === "PENDING" ? "Bridging… funds in transit" : "Confirming…");
    await new Promise((r) => setTimeout(r, intervalMs));
  }
  return { status: "TIMEOUT" };
}

// Poll both legs. The USDC leg is the primary "funds arrived" signal; gas is
// reported alongside. Resolves { usdc:{status,...}, gas:{status,...}|null }.
export async function pollZapStatus({ usdcTxHash, gasTxHash, fromChainId, onStep }) {
  const [usdc, gas] = await Promise.all([
    pollLeg({ txHash: usdcTxHash, fromChainId, onStep }),
    gasTxHash ? pollLeg({ txHash: gasTxHash, fromChainId }) : Promise.resolve(null),
  ]);
  return { usdc, gas };
}

export function supportedSourceChain(chainId) { return !!KNOWN_USDC[chainId]; }
